import React from 'react';
import d3, { useD3 } from 'hooks/useD3';
import { formatCurrency } from 'helpers/number';
import './index.css';

const COLORS = (index) => {
  const colors = ['#ffffff'];
  return colors[index % colors.length];
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

const BrushedBarChart = ({
  data = [],
  onItemClick,
  onItemMouseOver = () => null,
  onItemMouseOut = () => null,
  day = false,
  amountFormat = formatCurrency,
  idKey = 'id',
  valueKey = 'amount',
  defaultBarsToShow = 4,
}) => {
  const ref = useD3(
    (svg) => {
      const { width: windowWidth } = getWindowDimensions();
      svg.selectAll('*').remove();
      if (windowWidth < 960) {
        svg.attr('width', windowWidth * 0.95);
      }
      const mainSize = 0.8; // Percent of the main chart relative to the parent
      const margins = { top: 30, right: 80, bottom: 30, left: 80 }; // Margins for both charts
      const displayThreshold = 6;
      const mainPlacement = {
        top: margins.top,
        left: margins.left,
        height: parseInt(svg.attr('height')) * mainSize - margins.top - margins.bottom,
        width: +svg.attr('width') - margins.left - margins.right,
      };
      const miniPlacement = {
        top: mainPlacement.top + mainPlacement.height + margins.bottom + margins.top,
        height: parseInt(svg.attr('height')) * (1 - mainSize) - margins.top - margins.bottom,
      };

      const minBrush = (mainPlacement.width / (data.length || 1)) * defaultBarsToShow; // Minimum width the user can brush

      const x = d3.scaleBand().range([0, mainPlacement.width]).padding(0.1),
        x2 = d3.scaleBand().range([0, mainPlacement.width]).padding(0.1),
        y = d3.scaleLinear().range([mainPlacement.height, 0]),
        y2 = d3.scaleLinear().range([miniPlacement.height, 0]);

      const xAxis = d3.axisBottom(x);
      const yAxis = d3.axisLeft(y).tickFormat((d) => amountFormat(d, false));
      // const xAxis2 = d3.axisBottom(x2);

      let displayed;
      let currentRange;

      const dataLength = data.length;
      // const medianValue = d3.median(data.map((d) => d[valueKey]));
      const brush = d3
        .brushX()
        .extent([
          [0, 0],
          [mainPlacement.width, miniPlacement.height],
        ])
        .on('brush end', brushed);

      const zoom = d3
        .zoom()
        .scaleExtent([1, Infinity])
        .translateExtent([
          [0, 0],
          [mainPlacement.width, mainPlacement.height],
        ])
        .extent([
          [0, 0],
          [mainPlacement.width, mainPlacement.height],
        ]);

      svg
        .append('defs')
        .append('clipPath')
        .attr('id', 'clip')
        .append('rect')
        .attr('width', mainPlacement.width)
        .attr('height', mainPlacement.height);

      const main = svg
        .append('g')
        .attr('class', 'main')
        .style('filter', () => `drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.5)`)
        .attr('transform', 'translate(' + mainPlacement.left + ',' + mainPlacement.top + ')');

      const mini = svg
        .append('g')
        .attr('class', 'mini')
        .attr('transform', 'translate(' + margins.left + ',' + miniPlacement.top + ')');

      x.domain(data.map((ft) => ft[idKey]));
      y.domain([0, d3.max(data, (d) => d[valueKey])]);
      x2.domain(x.domain());
      y2.domain(y.domain());

      main
        .append('g')
        .attr('class', 'axis xAxis')
        .attr('transform', 'translate(0,' + mainPlacement.height + ')')
        .call(xAxis);

      main
        .select('.xAxis')
        .selectAll('text')
        .style('text-anchor', 'end')
        .style('filter', () => `drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.5)`)
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-65)');
      main.append('g').attr('class', 'axis yAxis').call(yAxis.tickSize(-mainPlacement.width, 0, 0));

      // const medianLine = main.append('g').attr('class', 'mean');

      // medianLine
      //   .append('text')
      //   .attr('x', 60)
      //   .attr('y', y(medianValue) + 20)
      //   .styles({
      //     fill: '#808080',
      //     'fill-opacity': '0.8',
      //     display: 'none',
      //   })
      //   .text('Median Quarter');

      // medianLine
      //   .append('line')
      //   .attr('x1', 0)
      //   .attr('x2', width)
      //   .attr('y1', y(medianValue))
      //   .attr('y2', y(medianValue))
      //   .style('stroke', '#808080');

      // medianLine
      //   .append('line')
      //   .attr('x1', 0)
      //   .attr('x2', width)
      //   .attr('y1', y(medianValue))
      //   .attr('y2', y(medianValue))
      //   .attr('stroke-width', '14px')
      //   .style('stroke', 'transparent')
      //   .on('mouseover', function () {
      //     medianLine.select('text').style('display', 'initial');
      //   })
      //   .on('mouseout', function () {
      //     medianLine.select('text').style('display', 'none');
      //   });

      updateMiniBars();

      mini.append('g').attr('class', 'brush').call(brush).call(brush.move, x.range());
      const tooltip = svg.append('g').attr('class', 'tooltip').style('display', 'none');

      tooltip
        .append('rect')
        .attr('width', 50)
        .attr('height', 40)
        .attr('fill', 'white')
        .style('opacity', 0.5);

      tooltip
        .append('text')
        .attr('class', 'id_feature')
        .attr('x', 25)
        .attr('dy', '1.2em')
        .style('text-anchor', 'middle')
        .attr('font-size', '14px');

      tooltip
        .append('text')
        .attr('class', 'value_feature')
        .attr('x', 25)
        .attr('dy', '2.4em')
        .style('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold');

      function updateMiniBars() {
        let miniChart = mini.selectAll('.bar').data(data);

        miniChart
          .attr('x', (d) => x2(d[idKey]))
          .attr('width', x2.bandwidth())
          .attr('y', (d) => y2(d[valueKey]))
          .attr('height', (d) => miniPlacement.height - y2(d[valueKey]))
          .style('fill', (d, i) => COLORS(i));

        miniChart
          .enter()
          .insert('rect')
          .attr('class', 'bar')
          .attr('x', (d) => x2(d[idKey]))
          .attr('width', x2.bandwidth())
          .attr('y', (d) => y2(d[valueKey]))
          .attr('height', (d) => miniPlacement.height - y2(d[valueKey]))
          .style('fill', (d, i) => COLORS(i));
        miniChart.exit().remove();
        mini.select('.xAxis').remove();
        // mini
        //   .append('g')
        //   .attr('class', 'axis xAxis')
        //   .attr('transform', 'translate(0,' + miniPlacement.height + ')')
        //   .call(xAxis2)
        //   .selectAll('text')
        //   .style('text-anchor', 'end')
        //   .attr('dx', '-.8em')
        //   .attr('dy', '.15em')
        //   .attr('transform', 'rotate(-65)');
      }

      function updateMain() {
        displayed = 0;
        let bar = main.selectAll('.bar').data(data);
        svg.selectAll('.bar-label').remove();

        bar
          .attr('x', (d) => x(d[idKey]))
          .attr('width', x.bandwidth())
          .attr('y', (d) => y(d[valueKey]))
          .attr('height', (d) => mainPlacement.height - y(d[valueKey]))
          .style('fill', (d, i) => COLORS(i))
          .style('display', (d) => {
            let to_display = x(d[idKey]) != null;
            if (to_display) {
              displayed += 1;
              return 'initial';
            }
            return 'none';
          })
          .style('cursor', () => (onItemClick ? 'pointer' : 'default'))
          .on('click', (d) => {
            if (onItemClick) {
              onItemClick(d);
            }
          })
          .append('text')
          .attr('y', (d) => y(d[valueKey]))
          .attr('x', (d) => x(d[idKey]))
          .attr('width', x.bandwidth());
        // .on('mouseover', () => {
        //   svg.select('.tooltip').style('display', null);
        // })
        // .on('mouseout', () => {
        //   svg.select('.tooltip').style('display', 'none');
        // })
        // .on('mousemove', function (d) {
        //   const tooltip = svg.select('.tooltip');
        //   tooltip.select('text.id_feature').text(`${d[idKey]}`);
        //   tooltip.select('text.value_feature').text(`${Math.round(d[valueKey])}`);
        //   tooltip.attr(
        //     'transform',
        //     `translate(${[d3.mouse(this)[0] - 5, d3.mouse(this)[1] - 25]})`,
        //   );
        // });

        // bar
        //   .enter()
        //   .append('text')
        //   .attr('y', (d, i) => y(d[valueKey]) - 10)
        //   .attr('x', (d, i) => {
        //     return x(d[idKey]) - x.bandwidth() * 2 - 20;
        //   })
        //   .style('display', (d, i) => {
        //     if (i === dataLength - 1 && displayed < dataLength - displayThreshold) {
        //       return '';
        //     } else {
        //       return 'none';
        //     }
        //   })
        //   .attr('font-size', '11px')
        //   .attr('class', 'bar-steve')
        //   // .attr('font-weight', '700')
        //   .style('filter', () => `drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.5)`)
        //   .attr('text-anchor', 'middle')
        //   .text((d, i) => amountFormat(d[valueKey]));

        bar
          .enter()
          .insert('rect', '.mean')
          .attr('class', 'bar bar-main')
          .attr('x', (d) => x(d[idKey]))
          .attr('width', x.bandwidth())
          .attr('y', (d) => y(d[valueKey]))
          .attr('height', (d) => mainPlacement.height - y(d[valueKey]));

        bar.exit().remove();
      }

      function updateAxis() {
        let axis_x = main.select('.xAxis').call(xAxis);
        axis_x
          .selectAll('text')
          .attr('dx', (d) => (displayed > displayThreshold ? '-0.8em' : '0'))
          .attr('dy', (d) => (displayed > displayThreshold ? '0.15em' : '0.71em'))
          .attr('transform', (d) => (displayed > displayThreshold ? 'rotate(-65)' : 'rotate(0)'))
          .style('text-anchor', (d) => (displayed > displayThreshold ? 'end' : 'middle'))
          .style('font-size', (d) => (displayed > displayThreshold ? '10px' : '12px'));
      }

      function updateMini(min, max) {
        mini.selectAll('.bar').style('fill-opacity', (_, i) => (i >= min && i < max ? '1' : '0.3'));
      }

      function brushed() {
        if (d3.event.sourceEvent && d3.event.sourceEvent.type === 'zoom') return; // ignore brush by zooming

        const s = d3.event.selection || x2.range();
        currentRange = [
          Math.round(s[0] / (mainPlacement.width / dataLength)),
          Math.round(s[1] / (mainPlacement.width / dataLength)),
        ];
        x.domain(data.slice(currentRange[0], currentRange[1]).map((ft) => ft[idKey]));
        svg
          .select('.zoom')
          .call(
            zoom.transform,
            d3.zoomIdentity
              .scale(mainPlacement.width / (currentRange[1] - currentRange[0]))
              .translate(-currentRange[0], 0),
          );
        updateMain();
        updateAxis();
        updateMini(currentRange[0], currentRange[1]);
      }
      d3.select('.brush').call(brush.move, [mainPlacement.width - minBrush, mainPlacement.width]);
    },
    [day, data],
  );
  return <svg ref={ref} id="BrushedBarChart" width="700" height="400" />;
};

export default BrushedBarChart;
